import { Link } from 'react-router-dom';
import { Film, Zap, Clock, Users, Car, Smartphone, CheckCircle, TrendingUp, Star, MessageCircle, Timer, BarChart3 } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const CinemaMultiplexParkingSystem = () => {
    useDynamicSEO({
      title: "Cinema & Multiplex Parking System | Peak-Hour Automation",
      description: "Accelerate entry & exit during show rush hours with a fast & intelligent Cinema & Multiplex Parking System. Reduce congestion & improve customer satisfaction.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "Cinema & Multiplex Parking System | Peak-Hour Automation",
      ogDescription: "Accelerate entry & exit during show rush hours with a fast & intelligent Cinema & Multiplex Parking System. Reduce congestion & improve customer satisfaction."
    });

  const handleWhatsAppClick = () => {
    const phoneNumber = '9384376329';
    const message = encodeURIComponent('Hi, I would like to request a demo of Park Hive Cinema & Multiplex Parking System for rush-hour efficiency and zero exit congestion management.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const multiplexChallenges = [
    "Entry rush 10 minutes before showtime",
    "Exit surge when multiple screens finish together",
    "Continuous cycle movement from morning to midnight",
    "Bike + car + cab + valet segregation",
    "Tight time-based billing accuracy",
    "Temporary team members and operator turnover",
    "Pressure to maximize vehicle turnover for business profitability"
  ];

  const keyFeatures = [
    {
      icon: <Zap className="w-8 h-8 text-yellow-600" />,
      title: "Lightning-fast Digital Scanning",
      description: "Instant entry & exit processing designed for showtime rush management"
    },
    {
      icon: <Timer className="w-8 h-8 text-blue-600" />,
      title: "Automatic Pricing Rules",
      description: "Time-based billing calculated instantly without manual intervention"
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-green-600" />,
      title: "Real-time Flow Monitoring",
      description: "Live slot availability and traffic flow management for optimal movement"
    },
    {
      icon: <Car className="w-8 h-8 text-purple-600" />,
      title: "Multi-category Support",
      description: "Valet, reserved, VIP, cab and season-pass parking coordination"
    },
    {
      icon: <Smartphone className="w-8 h-8 text-red-600" />,
      title: "Instant Digital Payments",
      description: "Lightning-fast settlements that eliminate exit delays and congestion"
    },
    {
      icon: <Users className="w-8 h-8 text-indigo-600" />,
      title: "Multi-level Synchronization",
      description: "Coordinate multiple gates and parking levels from unified dashboard"
    }
  ];

  const businessBenefits = [
    "Faster turn-around increases the number of vehicles per day",
    "Organized flow improves customer satisfaction and repeat visits",
    "Digital transparency eliminates revenue leakage",
    "Automated billing reduces manpower needs",
    "Real-time insights enable better staffing and pricing",
    "Smooth exit reduces risk of conflict and damage claims"
  ];

  const entertainmentVenues = [
    "Multiplexes & cinema chains",
    "Mall-integrated theatres", 
    "Drive-in theatres",
    "Live entertainment venues & auditoriums",
    "Music concerts & event halls"
  ];

  const industryReasons = [
    "Entertainment lives on reputation",
    "Parking pain kills customer loyalty",
    "Transparency builds trust", 
    "Perfect flow builds brand value"
  ];

  const faqData = [
    {
      question: "Will this reduce exit congestion after shows?",
      answer: "Yes — automated scanning moves vehicles continuously without stoppage."
    },
    {
      question: "Can this handle multiple levels and gates?",
      answer: "Absolutely — all operate under one synchronized system."
    },
    {
      question: "Does it support valet and reserved slots?",
      answer: "Yes — configuration is fully flexible."
    },
    {
      question: "Can owners track daily earnings in real time?",
      answer: "Yes — revenue dashboards update instantly."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 via-pink-800 to-red-900 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Film className="w-16 h-16 text-purple-300" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Cinema & Multiplex Parking System
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-purple-200">
            Rush-Hour Efficiency, Fast Entry & Zero Exit Congestion
          </p>
          <p className="text-xl mb-10 max-w-4xl mx-auto">
            High-speed parking system built for cinemas, multiplexes & entertainment complexes — engineered to handle showtime crowds like a perfectly timed performance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Request Live Demo
            </button>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-purple-900 px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 text-center"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* The Movie Experience Problem */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Parking Is the First and Last Scene of the Movie Experience
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Movie time is magic. People walk in excited, ready to escape reality. But nothing kills that excitement faster than a parking nightmare.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Parking Reality</h3>
              <p className="text-gray-700 mb-6">
                Long queues, arguments at the gate, ticket confusion, billing disputes, exit chaos after the show, and endless honking at midnight. 
                For cinema operators, this pressure happens every single show cycle.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Customers spend 20 minutes trying to park</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">30 minutes trying to get out after shows</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Bad parking becomes bad reviews</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Bad reviews become lost footfall and revenue</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-purple-600 mb-4">The Park Hive Transformation</h3>
              <p className="text-gray-700 mb-6">
                Parking is not a side service — it's the emotional trigger of the entire visit. 
                Park Hive turns parking into a smooth, fast, stress-free experience.
              </p>
              <div className="bg-purple-50 p-6 rounded-lg">
                <p className="font-semibold text-purple-800 text-center">
                  Guests remember parking with relief instead of anger.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Specialized System Needed */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Multiplex Parking Needs a Specialized System
            </h2>
            <p className="text-xl text-gray-700">
              Cinemas face challenges that ordinary parking operations never experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Unique Cinema Challenges</h3>
              <div className="space-y-4 mb-8">
                {multiplexChallenges.map((challenge, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{challenge}</p>
                  </div>
                ))}
              </div>
              <div className="bg-red-50 p-6 rounded-lg">
                <p className="text-red-800 font-semibold text-center">
                  Manual operations collapse under this intensity.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-green-800 mb-6">The Automation Advantage</h3>
              <p className="text-gray-700 mb-6">
                Every two to three hours thousands move in one wave and rush out in another. 
                If parking isn't fast and coordinated, the entire experience collapses.
              </p>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg text-center">
                  <Clock className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="font-bold text-green-800">Show-cycle Ready</p>
                </div>
                <div className="bg-white p-4 rounded-lg text-center">
                  <Zap className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="font-bold text-blue-800">Rush-hour Optimized</p>
                </div>
              </div>
              <div className="mt-6 bg-green-100 p-4 rounded-lg">
                <p className="font-bold text-green-800 text-center">Automation survives the intensity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Park Hive Transforms */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How Park Hive Transforms Multiplex Parking
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Real-time digital automation that handles rush-hour parking like a perfectly timed performance
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Instant Scanning</h3>
              <p className="text-gray-600 text-sm">Vehicle scanning starts timing immediately</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Auto Billing</h3>
              <p className="text-gray-600 text-sm">Pricing calculated automatically without delay</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Digital Payment</h3>
              <p className="text-gray-600 text-sm">Instant settlements without cash handling</p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Continuous Exit</h3>
              <p className="text-gray-600 text-sm">No gridlock, smooth vehicle flow</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
              Complete Multiplex Control
            </h3>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <Users className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Operators</h4>
                <p className="text-gray-700">Manage everything from single dashboard</p>
              </div>
              <div>
                <TrendingUp className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Owners</h4>
                <p className="text-gray-700">Track revenue live and prevent leakage</p>
              </div>
              <div>
                <Star className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Customers</h4>
                <p className="text-gray-700">Experience smooth, predictable flow</p>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-lg">
              <p className="text-xl font-bold text-center">
                Parking becomes performance — not panic.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Key Features Designed Specifically for Cinemas & Multiplexes
            </h2>
            <p className="text-xl text-gray-700">
              A system built to keep people moving — not waiting
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
              Advanced Multiplex Capabilities
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="text-center bg-white p-4 rounded-lg">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Operator Access Control</p>
              </div>
              <div className="text-center bg-white p-4 rounded-lg">
                <CheckCircle className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Peak-hour Optimization</p>
              </div>
              <div className="text-center bg-white p-4 rounded-lg">
                <CheckCircle className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Live Revenue Visibility</p>
              </div>
              <div className="text-center bg-white p-4 rounded-lg">
                <CheckCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Multi-screen Coordination</p>
              </div>
              <div className="text-center bg-white p-4 rounded-lg">
                <CheckCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Showtime Movement</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              Business Benefits for Parking Lot Owners
            </h2>
            <p className="text-xl text-purple-200">
              When performance improves, profit follows
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {businessBenefits.map((benefit, index) => (
              <div key={index} className="bg-white bg-opacity-20 p-6 rounded-xl">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-yellow-300 mt-1 flex-shrink-0" />
                  <p className="font-medium">{benefit}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white bg-opacity-20 p-8 rounded-xl text-center">
            <h3 className="text-2xl font-bold mb-6">Revenue Engine Transformation</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-xl font-bold mb-2 text-red-300">Before: Bottleneck</p>
                <p className="text-purple-200">Parking slows everything down</p>
              </div>
              <div>
                <p className="text-xl font-bold mb-2 text-green-300">After: Revenue Engine</p>
                <p className="text-purple-200">Parking drives profitability</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Entertainment Venues */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Built for High-Density Entertainment Environments
            </h2>
            <p className="text-xl text-gray-700">Perfect for time-bound crowds that move in waves</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {entertainmentVenues.map((venue, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
                <Film className="w-8 h-8 text-purple-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900">{venue}</h3>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white p-8 rounded-xl shadow-lg text-center">
            <p className="text-xl font-bold text-gray-900">
              Wherever time-bound crowds move in waves, efficiency is everything.
            </p>
          </div>
        </div>
      </section>

      {/* Why Industry Leaders Choose */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">
            Why Industry Leaders Choose Park Hive
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {industryReasons.map((reason, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl">
                <Star className="w-8 h-8 text-purple-600 mx-auto mb-4" />
                <p className="font-bold text-gray-900">{reason}</p>
              </div>
            ))}
          </div>

          <div className="bg-gray-900 text-white p-8 rounded-xl">
            <h3 className="text-2xl font-bold mb-6">Perfect Performance Management</h3>
            <p className="text-lg mb-6">
              Park Hive lets you run parking like a perfectly scheduled show — scene after scene, crowd after crowd, night after night.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Film className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="font-bold text-purple-400">Scene After Scene</p>
              </div>
              <div>
                <Users className="w-8 h-8 text-pink-400 mx-auto mb-2" />
                <p className="font-bold text-pink-400">Crowd After Crowd</p>
              </div>
              <div>
                <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="font-bold text-blue-400">Night After Night</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pink-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Experience Stress-Free Parking Automation
          </h2>
          <p className="text-xl mb-8">
            Watch the full parking cycle in motion. See peak exit flow without delays. Experience automation built for multiplex realities.
          </p>
          <button 
            onClick={handleWhatsAppClick}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            <MessageCircle className="w-5 h-5" />
            Request a Demo - Turn Parking Drama into Excellence
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-700">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Entertainment is about time and emotion.
          </h2>
          <div className="space-y-4 text-lg mb-8">
            <p>When parking runs smoothly, audiences walk out smiling — not stressed.</p>
            <p>When parking is chaos, the review begins at the gate — and it is never kind.</p>
            <p className="font-bold text-purple-400">Give them a smooth ending.</p>
            <p className="font-bold text-pink-400">Give them the perfect last scene.</p>
          </div>
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-xl">
            <p className="text-2xl font-bold">
              Park Hive makes parking feel effortless.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CinemaMultiplexParkingSystem;